import { Badge } from "@/shared/ui/badge";
import { Card, CardContent } from "@/shared/ui/card";

import type { GeoReport, GeoTopic } from "../../../api/geoService";

function priorityTone(p: GeoTopic["priority"]): string {
  if (p === "P0") return "bg-rose-500 text-white";
  if (p === "P1") return "bg-amber-500 text-white";
  return "bg-muted text-muted-foreground";
}

/** 内容策略卡「怎么写·怎么投」:选题 + 打点 + 渠道/节奏 + 优先级(全文代写留 AI记者)。 */
export function StrategyTopics({ report }: { report: GeoReport }) {
  const topics = report.recommendations?.topics ?? [];
  const legacyTitles = report.recommendations?.titles ?? [];
  if (topics.length === 0 && legacyTitles.length === 0) return null;
  return (
    <Card>
      <CardContent className="p-6">
        <div className="text-sm font-medium">怎么写 · 怎么投</div>
        <div className="mt-1 text-xs text-muted-foreground">
          优先瞄准「竞对被推荐而你缺席」的问题 —— 新内容要能直接成为那些问题的答案
        </div>
        {topics.length > 0 ? (
          <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-3" data-testid="geo-topics">
            {topics.map((t, i) => (
              <div
                key={i}
                className="flex flex-col gap-2 rounded-lg border border-border/60 bg-muted/30 p-3 text-sm"
              >
                <div className="flex items-start justify-between gap-2">
                  <span className="font-semibold leading-snug">{t.title}</span>
                  <Badge className={`${priorityTone(t.priority)} shrink-0`}>{t.priority}</Badge>
                </div>
                {t.target_question && (
                  <div className="rounded bg-rose-500/10 px-2 py-1 text-xs text-rose-500">
                    针对提问:「{t.target_question}」
                  </div>
                )}
                <ul className="list-disc space-y-1 pl-4 text-xs text-muted-foreground">
                  {t.points.map((p, j) => (
                    <li key={j}>{p}</li>
                  ))}
                </ul>
                {(t.outline?.length ?? 0) > 0 && (
                  <details className="text-xs">
                    <summary className="cursor-pointer select-none text-primary">
                      怎么写(结构建议)
                    </summary>
                    <ol className="mt-1 list-decimal space-y-0.5 pl-4 text-muted-foreground">
                      {t.outline!.map((o, j) => (
                        <li key={j}>{o}</li>
                      ))}
                    </ol>
                  </details>
                )}
                {(t.keywords?.length ?? 0) > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {t.keywords!.map((k) => (
                      <span
                        key={k}
                        className="rounded border border-border px-1.5 text-2xs text-muted-foreground"
                      >
                        植入:{k}
                      </span>
                    ))}
                  </div>
                )}
                <div className="mt-auto flex flex-wrap gap-1.5 text-xs">
                  <Badge variant="outline" className="font-normal">
                    投 {t.channel}
                  </Badge>
                  <Badge variant="outline" className="font-normal">
                    {t.timing}
                  </Badge>
                </div>
                {t.evidence && (
                  <div className="text-xs text-muted-foreground">依据:{t.evidence}</div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
            {legacyTitles.map((t, i) => (
              <li key={i}>{t}</li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
